import { Input as UIKitInput } from "@credit-acceptance/uikit";
import { type FocusEvent, useEffect, useRef, useState } from "react";

import { roundToDecimals } from "../../../lib/money";
import { cn } from "../../../lib/utils";
import { useComposedInputReference } from "./useComposedInputReference";

const PRECISION = 2;

interface CurrencyInputProperties {
  id?: string;
  name?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  value: number | null | undefined;
  onChange: (value: number | undefined) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  maxLength?: number;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

/** Strips to digits + a single decimal point, capped at PRECISION decimal digits. */
function sanitizeRawInput(raw: string): string {
  const digitsAndDot = raw.replaceAll(/[^\d.]/g, "");
  const firstDot = digitsAndDot.indexOf(".");
  if (firstDot === -1) {
    return digitsAndDot;
  }
  const intPart = digitsAndDot.slice(0, firstDot);
  const decimalPart = digitsAndDot
    .slice(firstDot + 1)
    .replaceAll(".", "")
    .slice(0, PRECISION);
  return `${intPart}.${decimalPart}`;
}

function parseRawInput(raw: string): number | undefined {
  if (raw === "" || raw === ".") {
    return undefined;
  }
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? undefined : roundToDecimals(parsed, PRECISION);
}

/** Comma-grouped, 2-decimal display formatting for a currency amount — exported for read-only currency displays (e.g. `LabeledValue`) that need the same formatting without a live-editable input. */
export function formatCurrencyForDisplay(value: number | null | undefined): string {
  if (value == null) {
    return "";
  }
  return roundToDecimals(value, PRECISION).toLocaleString("en-US", {
    minimumFractionDigits: PRECISION,
    maximumFractionDigits: PRECISION,
  });
}

/**
 * uikit's currency Input (InputNumber) reformats its display — commas plus
 * padded decimals — a second after any pause in typing, even while still
 * focused, by round-tripping the typed text through a JS number. A JS number
 * can't represent a trailing "." or trailing zeros after the decimal, so that
 * reformat silently strips them (e.g. typing "5." or "1.50", pausing, and
 * watching it collapse to "5" / "1.5"). None of InputNumber's props
 * (enableFormatting, onlyChangeOnBlur, padDecimals) stop that debounce from
 * firing, so it can't be fixed by configuring InputNumber.
 *
 * This is a plain-text `Input` instead, giving full control over display
 * timing: raw typed digits shown untouched while focused, reformatted only
 * on blur (or on an external value change while not focused).
 */
function CurrencyInput({ value, onChange, onFocus, onBlur, className, ...properties }: CurrencyInputProperties) {
  const [isFocused, setIsFocused] = useState(false);
  const [rawValue, setRawValue] = useState(() => formatCurrencyForDisplay(value));
  const inputReference = useComposedInputReference();
  const pendingCaretFixFrame = useRef<number | null>(null);

  useEffect(() => {
    if (!isFocused) {
      setRawValue(formatCurrencyForDisplay(value));
    }
  }, [value, isFocused]);

  function cancelPendingCaretFix() {
    if (pendingCaretFixFrame.current !== null) {
      cancelAnimationFrame(pendingCaretFixFrame.current);
      pendingCaretFixFrame.current = null;
    }
  }

  // Cancel on unmount so a still-pending fix never touches a detached node.
  useEffect(() => cancelPendingCaretFix, []);

  return (
    <UIKitInput
      kind="text"
      size="small"
      prefix="$"
      innerRef={inputReference}
      {...properties}
      className={cn("min-w-0", className)}
      value={rawValue}
      onChange={(next) => {
        const sanitized = sanitizeRawInput(next);
        setRawValue(sanitized);
        onChange(parseRawInput(sanitized));
      }}
      onFocus={(event: FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        const seeded = value == null ? "" : String(roundToDecimals(value, PRECISION));
        setRawValue(seeded);
        // On focus, the displayed value shrinks (formatted "5,000.00" becomes
        // raw "5000") — uikit's InputText mistakes any external value shrink
        // for a rejected keystroke and "restores" the cursor to wherever it
        // was during the last real keystroke (or 0, if there hasn't been one
        // yet) via its own requestAnimationFrame. A nested rAF runs one frame
        // later, after that correction has already applied, and moves the
        // caret to the end where a freshly-focused field is expected to land.
        // Skipped if a real keystroke landed in the meantime (the field's
        // value no longer matches what was seeded here) — that keystroke's
        // own cursor placement should win, not this correction.
        cancelPendingCaretFix();
        pendingCaretFixFrame.current = requestAnimationFrame(() => {
          pendingCaretFixFrame.current = requestAnimationFrame(() => {
            pendingCaretFixFrame.current = null;
            const input = inputReference.current;
            if (input && input.value === seeded) {
              const end = input.value.length;
              input.setSelectionRange(end, end);
            }
          });
        });
        onFocus?.();
        // uikit's InputText types onFocus loosely; touch the event so it stays a valid handler.
        void event;
      }}
      onBlur={() => {
        cancelPendingCaretFix();
        setIsFocused(false);
        onBlur?.();
      }}
    />
  );
}

export { CurrencyInput };
